import { computed, nextTick } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { useRoute } from '#imports'
import { shouldActivatePdlClickLock, shouldBlockButtonsAtStep } from '~/onboarding/clickLockRules'
import { pickEligibleTour } from '~/onboarding/tourSelection'
import type { OnboardingTourDefinition } from '~/onboarding/types'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { useOnboardingClickLock } from '~/composables/onboarding/useOnboardingClickLock'
import { AppCapability, userHasCapability } from '~~/utils/auth/roleCapabilityMatrix'
import { useOnboardingStatePersistence } from './useOnboardingStatePersistence'
import { useOnboardingDriverRuntime } from './useOnboardingDriverRuntime'
import { useOnboardingDomSync } from './useOnboardingDomSync'
import { useOnboardingAutoStart } from './useOnboardingAutoStart'
import { useOnboardingTrainingCenter } from './useOnboardingTrainingCenter'
import { useOnboardingTourSettings } from './useOnboardingTourSettings'

export type { OnboardingTrainingItem } from '~/onboarding/trainingItems'

type UseOnboardingOptions = {
  loggedIn: Ref<boolean>
  roles: Ref<string[]>
  canEditOrganigramOnCurrentView: Ref<boolean>
  toolboxOpen: Ref<boolean>
  isToolboxUiBlocked: Ref<boolean>
  isNoticeUpToDate: Ref<boolean>
  isEnvironmentNoticeOpen: Ref<boolean>
  isSeasonalWelcomeOpen: Ref<boolean>
  isWelcomeResolved: Ref<boolean>
  isAvatarModeResolved: Ref<boolean>
}

function playwrightGuideDisabled(): boolean {
  if (!import.meta.client) {
    return false
  }
  try {
    if (window.localStorage.getItem('monorga.playwright.disableOnboarding') === '1') {
      return true
    }
  } catch {
    // stockage indisponible
  }
  return new URLSearchParams(window.location.search).get('pw') === '1'
}

function computeLaunchBlockerReason(options: UseOnboardingOptions): string | null {
  if (!options.loggedIn.value) {
    return 'Connectez-vous pour lancer une formation.'
  }
  if (!options.isNoticeUpToDate.value) {
    return 'Signez la charte avant de lancer une formation.'
  }
  if (options.isEnvironmentNoticeOpen.value) {
    return 'Fermez la popup environnement de développement avant de lancer une formation.'
  }
  if (options.isSeasonalWelcomeOpen.value) {
    return 'Fermez la popup saisonnière avant de lancer une formation.'
  }
  if (!options.isWelcomeResolved.value) {
    return 'Patientez, l’écran de bienvenue est en cours de préparation.'
  }
  if (!options.isAvatarModeResolved.value) {
    return 'Choisissez votre mode d’avatar avant de lancer une formation.'
  }
  return null
}

/**
 * Pilote l’onboarding "Driver.js" de l’application : sélection du tour éligible sur la route
 * courante, cycle de vie de l’instance Driver.js, verrouillage des clics pendant certaines
 * étapes, et centre de formation ("Mes formations").
 *
 * La logique est répartie dans des composables dédiés pour rester lisible :
 * - `useOnboardingStatePersistence` : fusion / persistance de la progression (store, localStorage, API).
 * - `useOnboardingDriverRuntime` : démarrage / arrêt de l’instance Driver.js (anti-concurrence inclus).
 * - `useOnboardingDomSync` : synchronisation route / toolbox avec l’instance Driver.js active.
 * - `useOnboardingAutoStart` : déclenchement automatique d’un tour au chargement / changement de route.
 * - `useOnboardingTrainingCenter` : liste des formations disponibles + lancement manuel.
 */
export function useOnboarding(options: UseOnboardingOptions) {
  const route = useRoute()
  const toast = useSourceToast()
  const persistence = useOnboardingStatePersistence()
  const launchBlockerReason = computed(() => computeLaunchBlockerReason(options))
  const tourSettings = useOnboardingTourSettings()
  function toastInfo(title: string, message: string): void {
    toast.info(title, message)
  }
  const runtime = createOnboardingRuntime(options, persistence, route, toastInfo)
  const context = { options, route, persistence, runtime, tourSettings, launchBlockerReason }
  async function maybeStartTourOnCurrentRoute(): Promise<void> {
    await startEligibleTour(context)
  }
  useOnboardingDomSync({
    activeDriver: runtime.activeDriver,
    activeTourSteps: runtime.activeTourSteps,
    pendingTeleportIndex: runtime.pendingTeleportIndex,
    toolboxOpen: options.toolboxOpen,
    toolboxAnimationSyncTimer: runtime.toolboxAnimationSyncTimer,
    clearToolboxAnimationSyncTimer: runtime.clearToolboxAnimationSyncTimer,
    syncNextButtonDisabledState: runtime.syncNextButtonDisabledState,
  })
  bindOnboardingAutoStart(context, maybeStartTourOnCurrentRoute)
  const { availableTrainings, trainingsSummary, startTrainingById } = useOnboardingTrainingCenter({
    roles: options.roles,
    launchBlockerReason,
    resolveOnboardingStateMapForChecks: persistence.resolveOnboardingStateMapForChecks,
    toastInfo,
    stopCurrentTour: runtime.stopCurrentTour,
    startTour: runtime.startTour,
    resetSingleTourProgress: persistence.resetSingleTourProgress,
    disabledTourIds: tourSettings.disabledTourIds,
    isTourDisabled: tourSettings.isTourDisabled,
  })
  async function restartOnboarding(): Promise<void> {
    runtime.stopCurrentTour()
    persistence.clearAllProgress()
    await nextTick()
    await maybeStartTourOnCurrentRoute()
  }
  return {
    activeTourId: computed(() => runtime.activeTourId.value),
    maybeStartTourOnCurrentRoute,
    stopCurrentTour: runtime.stopCurrentTour,
    restartOnboarding,
    startTrainingById,
    launchBlockerReason,
    availableTrainings,
    trainingsSummary,
    onboardingState: computed(() => persistence.onboardingState.value),
    hasReadCapability: computed(() => userHasCapability(options.roles.value, AppCapability.ORG_READ)),
  }
}

type OnboardingContext = {
  options: UseOnboardingOptions
  route: ReturnType<typeof useRoute>
  persistence: ReturnType<typeof useOnboardingStatePersistence>
  runtime: ReturnType<typeof useOnboardingDriverRuntime>
  tourSettings: ReturnType<typeof useOnboardingTourSettings>
  launchBlockerReason: ComputedRef<string | null>
}

function createOnboardingRuntime(
  options: UseOnboardingOptions,
  persistence: OnboardingContext['persistence'],
  route: OnboardingContext['route'],
  toastInfo: (title: string, message: string) => void,
) {
  const { activateClickLock, activateBlockButtonsInActiveElement, deactivateClickLock } = useOnboardingClickLock()
  function onTourStepChange(tour: OnboardingTourDefinition, stepIndex: number): void {
    const nextState = persistence.updateTourProgress(tour, 'in_progress', stepIndex)
    persistence.persistOnboardingStateMap(nextState)
    const step = tour.steps[stepIndex]
    if (shouldActivatePdlClickLock(step, route.path)) {
      activateClickLock()
    } else if (shouldBlockButtonsAtStep(stepIndex)) {
      activateBlockButtonsInActiveElement()
    } else {
      deactivateClickLock()
    }
  }
  function onTourClosed(tour: OnboardingTourDefinition, isCompleted: boolean, stepIndex: number): void {
    if (isCompleted) {
      const nextState = persistence.updateTourProgress(tour, 'completed', stepIndex)
      persistence.persistOnboardingStateMap(nextState)
    }
    runtime.activeTourId.value = null
    runtime.pendingTeleportIndex.value = null
  }
  const runtime = useOnboardingDriverRuntime({
    toolboxOpen: options.toolboxOpen,
    isToolboxUiBlocked: options.isToolboxUiBlocked,
    toastInfo,
    resolveOnboardingStateMapForChecks: persistence.resolveOnboardingStateMapForChecks,
    updateTourProgress: persistence.updateTourProgress,
    persistOnboardingStateMap: persistence.persistOnboardingStateMap,
    onStepChange: onTourStepChange,
    onClosed: onTourClosed,
  })
  return runtime
}

async function startEligibleTour(context: OnboardingContext): Promise<void> {
  const { options, runtime, tourSettings, launchBlockerReason, persistence, route } = context
  if (import.meta.server) return
  if (playwrightGuideDisabled() || !options.loggedIn.value || launchBlockerReason.value !== null) {
    runtime.stopCurrentTour()
    return
  }
  if (!tourSettings.autoStartAllowed.value) return
  const activeId = runtime.activeTourId.value
  if (activeId !== null && tourSettings.isTourDisabled(activeId)) runtime.stopCurrentTour()
  if (runtime.activeDriver.value !== null && runtime.activeDriver.value.isActive()) return
  const candidate = pickEligibleTour(
    route.path,
    options.roles.value,
    options.canEditOrganigramOnCurrentView.value,
    persistence.resolveOnboardingStateMapForChecks(),
    tourSettings.disabledTourIds.value,
  )
  if (candidate !== null) await runtime.startTour(candidate)
}

function bindOnboardingAutoStart(context: OnboardingContext, maybeStartTourOnCurrentRoute: () => Promise<void>): void {
  const { options, persistence, tourSettings, launchBlockerReason } = context
  useOnboardingAutoStart({
    userPreferencesReady: persistence.userPreferencesReady,
    loggedIn: options.loggedIn,
    roles: options.roles,
    canEditOrganigramOnCurrentView: options.canEditOrganigramOnCurrentView,
    isNoticeUpToDate: options.isNoticeUpToDate,
    isEnvironmentNoticeOpen: options.isEnvironmentNoticeOpen,
    isSeasonalWelcomeOpen: options.isSeasonalWelcomeOpen,
    isWelcomeResolved: options.isWelcomeResolved,
    prefs: persistence.prefs,
    hasHydrated: persistence.hasHydrated,
    launchBlockerReason,
    toursReady: tourSettings.autoStartAllowed,
    disabledTourSignature: tourSettings.disabledTourSignature,
    syncStateFromStorageAndApi: persistence.syncStateFromStorageAndApi,
    maybeStartTourOnCurrentRoute,
  })
}
